import { isEmpty, remove } from "@utils/lodash";
import {
  cardDetailsParams,
  newCardDetailsParams,
  carDetailsCardV2Params,
  carDetailsCardV2PlansParams
} from "./mapping";
import metaData, { fieldToIconMetaV2 } from "./metaData";

const revisedNCBMetaData = (nonEmptyKeys, carJourneyData, hideNcb) => {
  const type = "ncb";
  let text = "";
  if (nonEmptyKeys.includes(type)) {
    remove(nonEmptyKeys, item => {
      return item === type;
    });

    if (
      carJourneyData.ncb.value &&
      !hideNcb &&
      carJourneyData.policyStatus?.value !== "90-"
    ) {
      text = `NCB - ${carJourneyData?.ncb?.displayValue}`;
    }
  }

  const item: any = {
    text,
    icon: metaData.lastPolicyClaim.icon,
    type,
    title: ""
  };

  const revisedResponse: any = {
    item,
    nonEmptyKeys
  };

  return revisedResponse;
};

const revisedCarNumberMetaData = (nonEmptyKeys, carJourneyData) => {
  let carNumber = "";
  if (nonEmptyKeys.includes("carNumber")) {
    carNumber = carJourneyData?.carNumber?.displayValue
      ? `${carJourneyData?.carNumber?.displayValue}`
      : "";
    remove(nonEmptyKeys, item => {
      return item === "carNumber";
    });
  }
  if (nonEmptyKeys.includes("mmv")) {
    const space = carNumber ? " " : "";
    const { mmv } = carJourneyData;
    carNumber += `${space}${mmv.make} ${mmv.model}`;
    remove(nonEmptyKeys, item => {
      return item === "mmv";
    });
  }

  const item: any = {
    text: carNumber,
    icon: metaData.carNumber.icon,
    type: "carNumber",
    title: ""
  };

  const revisedResponse: any = {
    item,
    nonEmptyKeys
  };

  return revisedResponse;
};

const revisedRegistrationTimeMetaData = (nonEmptyKeys, carJourneyData) => {
  let registrationTime = "";

  /**
   * Following string are possible
   * Registration in Month, 2018
   * Registration in Month
   * Registration in 2018
   * Note: doing it because of business & design requirements
   */

  const registrationYearAndMonthString =
    nonEmptyKeys.includes("registrationYear") &&
    nonEmptyKeys.includes("registrationMonth")
      ? true
      : "";

  if (registrationYearAndMonthString) {
    registrationTime =
      carJourneyData?.registrationYear?.displayValue &&
      carJourneyData?.registrationMonth?.displayValue
        ? `Registration in ${carJourneyData?.registrationMonth?.displayValue}, ${carJourneyData?.registrationYear?.displayValue}`
        : "";
    remove(nonEmptyKeys, item => {
      return item === "registrationYear";
    });
    remove(nonEmptyKeys, item => {
      return item === "registrationMonth";
    });
  } else {
    if (nonEmptyKeys.includes("registrationYear")) {
      registrationTime = carJourneyData?.registrationYear?.displayValue
        ? `Registration in ${carJourneyData?.registrationYear?.displayValue}`
        : "";
      remove(nonEmptyKeys, item => {
        return item === "registrationYear";
      });
    }

    if (nonEmptyKeys.includes("registrationMonth")) {
      registrationTime = `Registration in ${carJourneyData?.registrationMonth?.displayValue}`;
      remove(nonEmptyKeys, item => {
        return item === "registrationMonth";
      });
    }
  }

  const item: any = {
    text: registrationTime,
    icon: metaData.registrationYear.icon,
    type: "registrationYear",
    title: ""
  };

  const revisedResponse: any = {
    item,
    nonEmptyKeys
  };

  return revisedResponse;
};

export const getCarDetailsCompactVersion = ({
  carJourneyData = {},
  isPlans = false
}: any) => {
  let nonEmptyKeys: any = [];
  const metaConfig = isPlans
    ? carDetailsCardV2PlansParams
    : carDetailsCardV2Params;
  metaConfig.forEach(item => {
    if (item && carJourneyData[item]?.value) {
      //Fields massaging based on previous inputs
      if (
        // To ignore policyStatus answer wheen policy expiry is false
        !(
          item === "policyStatus" &&
          carJourneyData?.["expiry"].value === "false"
        )
      ) {
        nonEmptyKeys.push(item);
      }
    }
  });
  const dataList = nonEmptyKeys.map(key => {
    //Data massaging to accomodate compact version of details
    switch (key) {
      case "mmv":
        return {
          // title: carJourneyData[key].title,
          text: `${carJourneyData[key]?.make} ${carJourneyData[key]?.model}`,
          type: carJourneyData[key].type,
          icon: fieldToIconMetaV2(key)
        };
      case "registrationMonth":
        return {
          // title: carJourneyData[key].title,
          text: `${carJourneyData["registrationMonth"]?.displayValue}, ${carJourneyData["registrationYear"]?.value}`,
          type: carJourneyData[key].type,
          icon: fieldToIconMetaV2(key)
        };
      case "ncb":
        return {
          // title: carJourneyData[key].title,
          text: `NCB - ${carJourneyData[key]?.value}`,
          type: carJourneyData[key].type,
          icon: fieldToIconMetaV2(key)
        };
      default:
        return {
          // title: carJourneyData[key].title,
          text: carJourneyData[key]?.displayValue,
          type: carJourneyData[key].type,
          icon: fieldToIconMetaV2(key)
        };
    }
  });
  return dataList;
};

export const getCarDetails = ({
  isNewCar = false,
  carJourneyData = {},
  journeyConfig,
  hideNcb
}: any) => {
  let progress = 0;
  let totalMandatory = 0;
  const carParams = isNewCar ? newCardDetailsParams : cardDetailsParams;
  const nonEmptyKeys = Object.keys(carJourneyData).filter(item => {
    const value =
      carParams.includes(item) &&
      !isEmpty(carJourneyData[item]) &&
      carJourneyData[item]?.displayValue;

    if (
      !isEmpty(journeyConfig) &&
      journeyConfig[item] &&
      !carJourneyData[item].ignore
      // !journeyConfig[item].skip &&
      // !journeyConfig[item].optionDependency
    ) {
      if (!journeyConfig[item].optionDependency) {
        totalMandatory += !isEmpty(journeyConfig[item].optionDependants)
          ? 2
          : 1;
      }
      if (carJourneyData[item]?.displayValue) {
        progress += 1;
      }
    }

    const isValid =
      item === "policyStatus" &&
      carJourneyData?.policyStatus?.value &&
      carJourneyData?.policyStatus?.value[0] === "+"
        ? false
        : value;

    return value && isValid;
  });

  if (carJourneyData.externalCNGKit?.displayValue) {
    progress += 1;
  }

  const revisedCarNumberResponse = revisedCarNumberMetaData(
    nonEmptyKeys,
    carJourneyData
  );

  const revisedNCBResponse = revisedNCBMetaData(
    nonEmptyKeys,
    carJourneyData,
    hideNcb
  );

  const revisedRegistrationTime = revisedRegistrationTimeMetaData(
    nonEmptyKeys,
    carJourneyData
  );

  const otherItems = nonEmptyKeys.map(key => ({
    title: carJourneyData[key].title,
    text: carJourneyData[key]?.displayValue,
    type: carJourneyData[key].type,
    icon: (metaData as any)[key].icon
  }));
  let response: any = [];

  const isCarNumberPresent = revisedCarNumberResponse.item.text;
  const isNCBPresent = revisedNCBResponse.item.text;
  const isRegistrationTimePresent = revisedRegistrationTime.item.text;

  if (isCarNumberPresent) response.push(revisedCarNumberResponse.item);

  if (isNCBPresent && !isNewCar) response.push(revisedNCBResponse.item);

  if (isRegistrationTimePresent && !isNewCar)
    response.push(revisedRegistrationTime.item);

  response = [...response, ...otherItems];
  // if (totalMandatory > 0) {
  //   progress = (progress / totalMandatory) * 100;
  // }
  console.log({ carJourneyData });

  return { carDetails: response, progress, totalMandatory };
};

export const cleanRegNo = (carNumber: string) => {
  return carNumber?.toUpperCase()?.replace(/[. ,:-]+/g, "");
};

export const getVehicleImageURL = (relativeURL: string | null): string =>
  relativeURL ? `${process.env.MMV_ASSET_BASE}/${relativeURL}` : "";

export const getRootCard = ({ journeyConfig }) => {
  if (!isEmpty(journeyConfig)) {
    const root = Object.keys(journeyConfig).filter(key => {
      return journeyConfig[key].root;
    })[0];
    return root;
  }
  return "";
};

export const getJourneyItem = ({ item, journeyConfig, carJourneyData }) => {
  const { flag } = item ? journeyConfig[item] : { flag: null };
  if (flag) {
    let flagValue = "default";
    if (
      typeof carJourneyData[flag] !== "object" &&
      carJourneyData[flag] !== null &&
      carJourneyData[flag] !== undefined &&
      carJourneyData[flag] !== ""
    ) {
      flagValue = carJourneyData[flag].toString();
    } else if (
      typeof carJourneyData[flag] === "object" &&
      carJourneyData[flag] !== null &&
      carJourneyData[flag].value !== null &&
      carJourneyData[flag].value !== undefined &&
      carJourneyData[flag].value !== ""
    ) {
      flagValue = carJourneyData[flag].value.toString();
    }
    return journeyConfig[item].types[flagValue];
  }
  return item;
};
